import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { Check, Filter, ChevronDown, ChevronUp } from "lucide-react";
import { format, parseISO } from "date-fns";
import * as XLSX from "xlsx";
import Select from "react-select";
import { isWithinInterval, startOfDay, endOfDay } from "date-fns";
import { MousePointer } from "lucide-react";

const adminLocation = Cookies.get("adminLocation") || "";

const EVENT_TYPES = [
  { id: "1", label: "Demo Cars", value: "0" },
  { id: "2", label: "Pre-Owned Cars", value: "1" },
];

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);
  return isMobile;
};

const WeeklyCalendar = ({
  selectedDate,
  onDateSelect,
  availableSlots,
  selectedVehicles,
  allVehicles,
}) => {
  const isMobile = useIsMobile();
  const weekDays = Object.keys(availableSlots)
    .sort((a, b) => new Date(a) - new Date(b))
    .map((date) => parseISO(date));

  const countAvailableVehicles = (date) => {
    let count = 0;
    selectedVehicles.forEach((vehicleId) => {
      const vehicle = allVehicles.find((v) => v.AuctionListId === vehicleId);
      if (vehicle && vehicle.availableEndDateAndTime) {
        const vehicleEndTime = new Date(vehicle.availableEndDateAndTime);
        const selectedDate = new Date(date);
        if (vehicleEndTime >= selectedDate) {
          count++;
        }
      }
    });
    return count;
  };

  return (
    <div
      className={`d-flex ${
        isMobile ? "flex-wrap justify-content-center" : ""
      } gap-2`}
      style={{ overflowX: "auto", maxWidth: "100%" }}
    >
      {weekDays.map((date) => {
        const dateKey = format(date, "yyyy-MM-dd");
        const hasSlots = availableSlots[dateKey]?.length > 0;
        const selectedCarCount = countAvailableVehicles(date);
        const isSelected = selectedDate?.toDateString() === date.toDateString();
        return (
          <button
            key={dateKey}
            onClick={() => hasSlots && onDateSelect(date)}
            disabled={!hasSlots}
            className={`text-black ${
              isSelected ? "text-white" : "hover:bg-light"
            }`}
            style={{
              minWidth: isMobile ? "80px" : "100px",
              padding: isMobile ? "8px" : "16px",
              border: hasSlots ? "2px solid #ee373b" : "none",
              borderRadius: "10px",
              backgroundColor: isSelected ? "#ee373b" : "white",
              opacity: hasSlots ? 1 : 0.5,
              cursor: hasSlots ? "pointer" : "not-allowed",
              fontSize: "var(--base-font-size)",
            }}
          >
            <div className={isMobile ? "text-xs" : "text-sm"}>
              {format(date, "EEE")}
            </div>
            <div className={isMobile ? "text-lg" : "text-xl font-semibold"}>
              {format(date, "d")}
            </div>
            <div className={isMobile ? "text-xs" : "text-sm"}>
              {!isMobile ? "Available" : ""} Count: {selectedCarCount}
            </div>
          </button>
        );
      })}
    </div>
  );
};

const TimeSlots = ({ timeSlots, onTimeSlotSelect }) => {
  return (
    <div
      className="d-flex flex-wrap gap-2 mt-6 mx-lg-10"
      style={{ overflowY: "auto" }}
    >
      {timeSlots.map((slot, index) => (
        <button
          key={slot.time}
          onClick={() => onTimeSlotSelect(index)}
          className={`p-3 transition ${
            slot.selected ? "text-white" : "text-black"
          }`}
          style={{
            border: "2px solid #ee373b",
            backgroundColor: slot.selected ? "#ee373b" : "white",
            borderRadius: "5px",
            fontSize: "var(--base-font-size)",
          }}
        >
          {slot.time}
        </button>
      ))}
    </div>
  );
};

const TextDisplayCopy = ({ link }) => {
  const [text, setText] = useState(link);
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1500);
    });
  };

  const styles = {
    container: {
      maxWidth: "800px",
      margin: "0 auto",
      display: "flex",
      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
    },
    textDisplay: {
      flexGrow: 1,
      padding: "12px",
      backgroundColor: "white",
      border: "1px solid #ee373b",
      borderRight: "none",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      color: "#333",
      fontSize: "var(--base-font-size)",
    },
    copyButton: {
      width: "48px",
      padding: "12px",
      backgroundColor: isCopied ? "#f59e0b" : "#ee373b",
      color: "white",
      border: "none",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "background-color 0.3s ease",
    },
    icon: {
      width: "20px",
      height: "20px",
      animation: isCopied ? "pulse 1.5s ease" : "none",
    },
  };

  return (
    <>
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          25% { transform: scale(1.2); }
          50% { transform: scale(1); }
          75% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div style={styles.container}>
        <div style={styles.textDisplay}>{text}</div>
        <button
          onClick={handleCopy}
          style={styles.copyButton}
          aria-label="Copy text"
        >
          {isCopied ? (
            <svg
              style={styles.icon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          ) : (
            <svg
              style={styles.icon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
              ></path>
            </svg>
          )}
        </button>
      </div>
    </>
  );
};

const VehicleSelectionEventCreation = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedVehicles, setSelectedVehicles] = useState(new Set());
  const [allVehicles, setAllVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filters, setFilters] = useState({
    vehicleCategory: "pre-owned",
    makes: [],
    models: [],
    years: [],
    maxPrice: "",
    location: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    makes: [],
    models: [],
    years: [],
    locations: [],
  });
  const [formData, setFormData] = useState({
    selectedDate: null,
    selectedTimeSlots: null,
    approvalLimit: "2000000",
    duration: "",
  });
  const [isComplete, setIsComplete] = useState(false);
  const [availableSlots, setAvailableSlots] = useState({});
  const [users, setUsers] = useState([]);
  const [communityNo, setCommunityNo] = useState(null);
  const [eventTitle, setEventTitle] = useState("");
  const [location, setLocation] = useState("");
  const [carCount, setCarCount] = useState(0);
  const [carCountPerDate, setCarCountPerDate] = useState({});
  const [link, setLink] = useState("");
  const [isStepValid, setIsStepValid] = useState(false);
  const [durationOptions, setDurationOptions] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    userID: "",
    email: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [vehicleType, setVehicleType] = useState("1");
  const [eventType, setEventType] = useState("open");
  const [inputMethod, setInputMethod] = useState("manual");
  const [maxStartPrice, setMaxStartPrice] = useState(0);
  const [selectedVehiclesForAPI, setSelectedVehiclesForAPI] = useState([]);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedVehicles.size > 0) {
      const selectedVehiclesDetails = allVehicles.filter((v) =>
        selectedVehicles.has(v.AuctionListId)
      );
      if (selectedVehiclesDetails.length > 0) {
        const maxPrice = Math.max(
          ...selectedVehiclesDetails.map((v) => parseFloat(v.StartPrice) || 0)
        );
        setMaxStartPrice(maxPrice);
      } else {
        setMaxStartPrice(0);
      }
    } else {
      setMaxStartPrice(0);
    }
  }, [selectedVehicles, allVehicles]);

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return selectedVehicles.size > 0;
      case 2:
        return (
          formData.selectedDate !== null &&
          formData.selectedTimeSlots?.some((slot) => slot.selected)
        );
      case 3:
        return formData.approvalLimit !== "" && formData.duration !== "";
      case 4:
        return users.length > 0;
      case 5:
        return (
          selectedVehicles.size > 0 &&
          formData.selectedDate !== null &&
          formData.selectedTimeSlots?.some((slot) => slot.selected) &&
          formData.approvalLimit !== "" &&
          formData.duration !== "" &&
          users.length > 0
        );
      default:
        return false;
    }
  };

  useEffect(() => {
    setIsStepValid(validateStep());
  }, [currentStep, selectedVehicles, formData, users]);

  useEffect(() => {
    fetchVehicles();
    const communityNumber = parseInt(Cookies.get("adminCommunityNO"), 10);
    setCommunityNo(communityNumber);
    const lenderInfoRaw = Cookies.get("adminLenderInfo") || "";
    const [firstName = "", lastName = ""] = lenderInfoRaw.split("_");
    const lenderInfo = `${firstName} ${lastName}`.trim();
    const loc = Cookies.get("adminLocation") || "";
    setLocation(loc);
    const eventTitles = `${lenderInfo} ${loc}${
      vehicleType == "0" ? " - DEMO CARS" : ""
    }`.trim();
    setEventTitle(eventTitles);
    fetchAvailableSlots(loc);
  }, [vehicleType]);

  const computeFilterOptions = () => {
    let filteredForMakes = [...allVehicles];
    let filteredForModels = [...allVehicles];
    let filteredForYears = [...allVehicles];
    let filteredForLocations = [...allVehicles];
    const { vehicleCategory, makes, models, years, maxPrice, location } =
      filters;
    if (vehicleCategory !== "all") {
      const category = vehicleCategory === "demo" ? "Demo" : "Pre-Owned";
      filteredForMakes = filteredForMakes.filter(
        (vehicle) => vehicle.VehicleCategory === category
      );
    }
    if (models.length > 0) {
      filteredForMakes = filteredForMakes.filter((vehicle) =>
        models.includes(vehicle.Model)
      );
    }
    if (years.length > 0) {
      filteredForMakes = filteredForMakes.filter((vehicle) =>
        years.includes(vehicle.Year)
      );
    }
    if (maxPrice) {
      filteredForMakes = filteredForMakes.filter(
        (vehicle) => parseFloat(vehicle.StartPrice) <= parseFloat(maxPrice)
      );
    }
    if (location) {
      filteredForMakes = filteredForMakes.filter((vehicle) =>
        vehicle.Location?.toLowerCase().includes(location.toLowerCase())
      );
    }
    if (vehicleCategory !== "all") {
      const category = vehicleCategory === "demo" ? "Demo" : "Pre-Owned";
      filteredForModels = filteredForModels.filter(
        (vehicle) => vehicle.VehicleCategory === category
      );
    }
    if (makes.length > 0) {
      filteredForModels = filteredForModels.filter((vehicle) =>
        makes.includes(vehicle.Make)
      );
    }
    if (years.length > 0) {
      filteredForModels = filteredForModels.filter((vehicle) =>
        years.includes(vehicle.Year)
      );
    }
    if (maxPrice) {
      filteredForModels = filteredForModels.filter(
        (vehicle) => parseFloat(vehicle.StartPrice) <= parseFloat(maxPrice)
      );
    }
    if (location) {
      filteredForModels = filteredForModels.filter((vehicle) =>
        vehicle.Location?.toLowerCase().includes(location.toLowerCase())
      );
    }
    if (vehicleCategory !== "all") {
      const category = vehicleCategory === "demo" ? "Demo" : "Pre-Owned";
      filteredForYears = filteredForYears.filter(
        (vehicle) => vehicle.VehicleCategory === category
      );
    }
    if (makes.length > 0) {
      filteredForYears = filteredForYears.filter((vehicle) =>
        makes.includes(vehicle.Make)
      );
    }
    if (models.length > 0) {
      filteredForYears = filteredForYears.filter((vehicle) =>
        models.includes(vehicle.Model)
      );
    }
    if (maxPrice) {
      filteredForYears = filteredForYears.filter(
        (vehicle) => parseFloat(vehicle.StartPrice) <= parseFloat(maxPrice)
      );
    }
    if (location) {
      filteredForYears = filteredForYears.filter((vehicle) =>
        vehicle.Location?.toLowerCase().includes(location.toLowerCase())
      );
    }
    if (vehicleCategory !== "all") {
      const category = vehicleCategory === "demo" ? "Demo" : "Pre-Owned";
      filteredForLocations = filteredForLocations.filter(
        (vehicle) => vehicle.VehicleCategory === category
      );
    }
    if (makes.length > 0) {
      filteredForLocations = filteredForLocations.filter((vehicle) =>
        makes.includes(vehicle.Make)
      );
    }
    if (models.length > 0) {
      filteredForLocations = filteredForLocations.filter((vehicle) =>
        models.includes(vehicle.Model)
      );
    }
    if (years.length > 0) {
      filteredForLocations = filteredForLocations.filter((vehicle) =>
        years.includes(vehicle.Year)
      );
    }
    if (maxPrice) {
      filteredForLocations = filteredForLocations.filter(
        (vehicle) => parseFloat(vehicle.StartPrice) <= parseFloat(maxPrice)
      );
    }
    return {
      makes: [
        ...new Set(filteredForMakes.map((v) => v.Make).filter(Boolean)),
      ].sort(),
      models: [
        ...new Set(filteredForModels.map((v) => v.Model).filter(Boolean)),
      ].sort(),
      years: [
        ...new Set(filteredForYears.map((v) => v.Year).filter(Boolean)),
      ].sort((a, b) => b - a),
      locations: [
        ...new Set(filteredForLocations.map((v) => v.Location).filter(Boolean)),
      ].sort(),
    };
  };

  useEffect(() => {
    const options = computeFilterOptions();
    setFilterOptions(options);
  }, [filters, allVehicles]);

  const clearAllFilters = () => {
    setFilters({
      vehicleCategory: "pre-owned",
      makes: [],
      models: [],
      years: [],
      maxPrice: "",
      location: "",
    });
    setSelectedVehicles(new Set());
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_ENDPOINT}/api/lender/car/list`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to fetch vehicles");
      const data = await response.json();
      const demoCars = data.demoCars || [];
      const preOwnedCars = data.preOwnedCars || [];
      const flattened = [...demoCars, ...preOwnedCars];
      const toIST = (date) => {
        const istOffset = 5.5 * 60 * 60 * 1000;
        return new Date(date.getTime() + istOffset);
      };
      const today = toIST(new Date());
      today.setHours(0, 0, 1, 0);
      const mapped = flattened
        .map((vehicle) => ({
          AuctionListId: vehicle.AuctionListId,
          RegistrationNo: vehicle.RegistrationNo,
          Make: vehicle.make,
          Model: vehicle.model,
          Variant: vehicle.variant,
          Year: vehicle.year,
          Color: vehicle.color,
          StartPrice: vehicle.StartPrice,
          Mileage: vehicle.Milage,
          Location: vehicle.Location,
          VehicleCategory: vehicle.VehicleCategory,
          availableStartDateAndTime: vehicle.availableStartDateAndTime,
          availableEndDateAndTime: vehicle.availableEndDateAndTime,
        }))
        .filter((vehicle) => {
          const startDate = new Date(vehicle.availableStartDateAndTime);
          const endDate = new Date(vehicle.availableEndDateAndTime);
          return startDate <= today && endDate >= today;
        });
      setAllVehicles(mapped);
    } catch (err) {
      setError(err.message || "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (loc) => {
    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_ENDPOINT
        }/api/lender/lender-events/showAvailableSlotsForNextWeek?location=${
          loc}`,
        {
          credentials: "include",
          headers: {
            "X-Full-URL": window.location.href,
          },
        }
      );
      const data = await response.json();
      setAvailableSlots(data);
      const countPerDate = {};
      Object.keys(data).forEach(dateKey => {
        const slots = data[dateKey];
        if (slots && slots.length > 0) {
          countPerDate[dateKey] = slots[0]?.carCountBelow1176 || 0;
        }
      });
      setCarCountPerDate(countPerDate);
    } catch (error) {
      console.error("Error fetching available slots:", error);
    }
  };

  const filteredVehicles = allVehicles.filter((vehicle) => {
    if (filters.vehicleCategory !== "all") {
      const category =
        filters.vehicleCategory === "demo" ? "Demo" : "Pre-Owned";
      if (vehicle.VehicleCategory !== category) return false;
    }
    if (filters.makes.length > 0 && !filters.makes.includes(vehicle.Make))
      return false;
    if (filters.models.length > 0 && !filters.models.includes(vehicle.Model))
      return false;
    if (filters.years.length > 0 && !filters.years.includes(vehicle.Year))
      return false;
    if (
      filters.maxPrice &&
      parseFloat(vehicle.StartPrice) > parseFloat(filters.maxPrice)
    )
      return false;
    if (
      filters.location &&
      !vehicle.Location?.toLowerCase().includes(filters.location.toLowerCase())
    )
      return false;
    if (adminLocation && !vehicle.Location?.toLowerCase().includes(adminLocation.toLowerCase())) return false;
    return true;
  });

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setSelectedVehicles(new Set());
  };

  const handleVehicleSelect = (auctionListId) => {
    setSelectedVehicles((prev) => {
      const next = new Set(prev);
      if (next.has(auctionListId)) next.delete(auctionListId);
      else next.add(auctionListId);
      return next;
    });
  };

  const selectAllVisible = () =>
    setSelectedVehicles(new Set(filteredVehicles.map((v) => v.AuctionListId)));

  const deselectAll = () => setSelectedVehicles(new Set());

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((prev) => prev + 1);
    } else {
      handleCreateEvent();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const isVehicleAvailableOnDate = (vehicle, date) => {
    if (!vehicle.availableStartDateAndTime || !vehicle.availableEndDateAndTime) {
      return false;
    }
    const vehicleStartTime = new Date(vehicle.availableStartDateAndTime);
    const vehicleEndTime = new Date(vehicle.availableEndDateAndTime);
    return isWithinInterval(date, {
      start: startOfDay(vehicleStartTime),
      end: endOfDay(vehicleEndTime),
    });
  };

  const handleDateSelect = (date) => {
    const dateKey = format(date, "yyyy-MM-dd");
    const slotsForDate = availableSlots[dateKey] || [];
    const carCount = slotsForDate[0]?.carCountBelow1176 || 0;
    const selectedVehiclesList = Array.from(selectedVehicles);
    const availableSelectedVehicles = allVehicles.filter(vehicle =>
      selectedVehiclesList.includes(vehicle.AuctionListId) &&
      isVehicleAvailableOnDate(vehicle, date)
    );
    setSelectedVehiclesForAPI(availableSelectedVehicles);
    setCarCount(carCount);
    const formattedTimeSlots = slotsForDate.map((slot) => ({
      time: slot.availebleSlot,
      selected: false,
      id: slot.slotId,
    }));
    setFormData((prev) => ({
      ...prev,
      selectedDate: date,
      selectedTimeSlots: formattedTimeSlots,
    }));
  };

  const handleTimeSlotSelect = (index) => {
    const updatedSlots = formData.selectedTimeSlots.map((slot, i) => ({
      ...slot,
      selected: i === index,
    }));
    setFormData((prev) => ({
      ...prev,
      selectedTimeSlots: updatedSlots,
    }));
    setFormData((prev) => ({
      ...prev,
      duration: "",
    }));
  };

  const handleDurationChange = (e) => {
    setFormData((prev) => ({ ...prev, duration: e.target.value }));
  };

  // const handleAddUser = () => {
  //   const error = validateUser(newUser.name, newUser.userID, newUser.email);
  //   if (error) {
  //     setErrorMessage(error);
  //     return;
  //   }
  //   const userExists = users.some((user) => user.userID === newUser.userID);
  //   if (userExists) {
  //     setErrorMessage("User with this phone number already exists");
  //     return;
  //   }
  //   setUsers([
  //     ...users,
  //     { name: newUser.name, userID: newUser.userID, email: newUser.email },
  //   ]);
  //   setNewUser({ name: "", userID: "", email: "" });
  //   setErrorMessage("");
  // };

  // const validateUser = (name, userID, email) => {
  //   if (!name.trim()) return "Name is required";
  //   if (!userID.trim()) return "Phone number is required";
  //   if (!/^\d{10}$/.test(userID)) return "Phone number must be 10 digits";
  //   if (!email.trim()) return "Email is required";
  //   if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
  //     return "Invalid email format";
  //   return "";
  // };

  // In the VehicleSelectionEventCreation component, update the newUser state and validation:

  const validateUser = (name, userID, email) => {
    if (!name.trim()) return "Name is required";
    if (!userID.trim()) return "Phone number is required";
    if (!/^\d{10}$/.test(userID)) return "Phone number must be 10 digits";
    // Email is optional, so no validation required
    return "";
  };

  // In the handleAddUser function:
  const handleAddUser = () => {
    const error = validateUser(newUser.name, newUser.userID, newUser.email);
    if (error) {
      setErrorMessage(error);
      return;
    }
    const userExists = users.some((user) => user.userID === newUser.userID);
    if (userExists) {
      setErrorMessage("User with this phone number already exists");
      return;
    }
    setUsers([
      ...users,
      {
        name: newUser.name,
        userID: newUser.userID,
        email: newUser.email || "",
      },
    ]);
    setNewUser({ name: "", userID: "", email: "" });
    setErrorMessage("");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      if (rawData.length < 2) {
        setErrorMessage("No valid data found in the Excel file.");
        return;
      }
      const headers = rawData[0].map((h) => String(h).toLowerCase().trim());
      const nameIndex = headers.findIndex((h) =>
        ["name", "customer name"].includes(h)
      );
      const phoneIndex = headers.findIndex((h) =>
        ["phone", "phone number", "contact", "mobile"].includes(h)
      );
      const emailIndex = headers.findIndex((h) =>
        ["email", "email id", "e-mail"].includes(h)
      );
      if (nameIndex === -1 || phoneIndex === -1) {
        setErrorMessage(
          "Excel file must contain 'Name' and 'Phone' columns (case-insensitive)."
        );
        return;
      }
      const parsedUsers = rawData
        .slice(1)
        .map((row) => {
          const name = row[nameIndex]?.toString().trim() || "";
          const userID = row[phoneIndex]?.toString().trim() || "";
          const email = row[emailIndex]?.toString().trim() || "";
          const error = validateUser(name, userID, email);
          if (!error) {
            return { name, userID, email: email || "" };
          }
          return null;
        })
        .filter(Boolean);
      if (parsedUsers.length === 0) {
        setErrorMessage("No valid users found in the Excel file.");
        return;
      }
      setUsers(parsedUsers);
      setErrorMessage("");
    };
    reader.readAsArrayBuffer(file);
  };

  const handleCreateEvent = () => {
    setIsCreating(true);
    const selectedTimeSlot = formData.selectedTimeSlots.find(
      (slot) => slot.selected
    );
    if (!selectedTimeSlot) {
      console.error("No time slot selected");
      setIsCreating(false);
      return;
    }
    const availableAuctionListIds = selectedVehiclesForAPI.map(
      (v) => v.AuctionListId
    );
    const startDate = new Date(formData.selectedDate);
    const [hours, minutes] = selectedTimeSlot.time.split(":").map(Number);
    startDate.setHours(hours, minutes, 0, 0);
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + parseInt(formData.duration, 10));
    const formatDateTime = (date) => {
      const pad = (n) => n.toString().padStart(2, "0");
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };
    const eventData = {
      eventTitle: eventTitle.replace(/[^a-zA-Z0-9 ]/g, ""),
      location: location,
      startDateAndTime: formatDateTime(startDate),
      endDateAndTime: formatDateTime(endDate),
      userApprovedLimit: maxStartPrice.toString(),
      communityNo: communityNo,
      auctionSlotId: selectedTimeSlot.id,
      duration: formData.duration,
      eventType: vehicleType,
      isPreApproved: false,
      isWhitelisted: isWhitelisted,
    };
    const usersForBackend = users.map(user => ({
      name: user.name,
      userID: user.userID,
      preapprovedLimitValue: maxStartPrice,
      email: user.email
    }));
    const usersJson = JSON.stringify(usersForBackend);
    const eventDetailsJson = JSON.stringify(eventData);
    const selectedVehicleIdsJson = JSON.stringify(availableAuctionListIds);
    const formDataToSend = new FormData();
    formDataToSend.append("communityNo", communityNo);
    formDataToSend.append("users", usersJson);
    formDataToSend.append("eventDetails", eventDetailsJson);
    formDataToSend.append("vehicleType", Number(vehicleType));
    formDataToSend.append("isPreApproved", false);
    formDataToSend.append("selectedVehicleIds", selectedVehicleIdsJson);
    fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/api/lender/lender-events/privateLender/createEventDSA`,
      {
        method: "POST",
        body: formDataToSend,
        credentials: "include",
        headers: {
          "X-Created-By": "DSA",
        },
      }
    )
      .then((response) => response.json())
      .then((data) => {
        if (data.message && data.message.includes("Event created successfully")) {
          setLink(data.link || "");
          setIsComplete(true);
        } else {
          console.error("Error creating event:", data);
        }
      })
      .catch((error) => {
        console.error("Error creating event:", error);
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderColor: "#ee373b",
      zIndex: "9999",
      "&:hover": {
        borderColor: "#ee373b",
      },
      minHeight: "38px",
      fontSize: "var(--base-font-size)",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#ee373b"
        : state.isFocused
        ? "#fee2e2"
        : "white",
      color: state.isSelected ? "white" : "black",
      fontSize: "var(--base-font-size)",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10000,
    }),
    menuList: (base) => ({
      ...base,
      maxHeight: "150px",
      overflowY: "auto",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "#fee2e2",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#ee373b",
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: "#ee373b",
      "&:hover": {
        backgroundColor: "#ee373b",
        color: "white",
      },
    }),
  };

  const extraStyles = `
    :root {
      --base-font-size: 14px;
    }
    body {
      font-size: var(--base-font-size) !important;
    }
    h1, h2, h3, h4, h5, h6, p, span, label, small, button, input, select, option, td, th, div, a {
      font-size: var(--base-font-size) !important;
    }
    .custom-header{
      font-size: 25px !important;
    }
    .h2 {
      font-size: calc(var(--base-font-size) * 1.5) !important;
    }
    .text-sm {
      font-size: calc(var(--base-font-size) * 0.875) !important;
    }
    .text-xs {
      font-size: calc(var(--base-font-size) * 0.75) !important;
    }
    .text-lg {
      font-size: calc(var(--base-font-size) * 1.25) !important;
    }
    .filter-section {
      transition: all 0.3s ease;
      max-height: none;
    }
    @media (min-width: 768px) {
      .filter-section:hover {
        max-height: none;
      }
    }
    .filter-section.filter-section-expanded {
      max-height: none;
    }
    .table-fixed-header {
      position: sticky;
      top: 0;
      z-index: 10;
      background-color: #f8f9fa;
    }
    .table-scrollable-body {
      display: block;
      max-height: 15vh;
      overflow-y: auto;
      scrollbar-color: #ee373b #ffffff;
      scrollbar-width: thin;
    }
    .table-scrollable-body::-webkit-scrollbar {
      width: 8px;
    }
    .table-scrollable-body::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    .table-scrollable-body::-webkit-scrollbar-thumb {
      background: #ee373b;
      border-radius: 4px;
    }
    .table-scrollable-body::-webkit-scrollbar-thumb:hover {
      background: #cc2e32;
    }
    .table-wrapper table {
      width: 100%;
    }
    .table-wrapper thead {
      display: table;
      width: calc(100% - 8px);
      table-layout: fixed;
    }
    .table-wrapper tbody {
      display: block;
      max-height: 22.5vh;
      overflow-y: auto;
    }
    .table-wrapper tbody tr {
      display: table;
      width: 100%;
      table-layout: fixed;
    }
    .btn-primary-custom {
      background-color: #ee373b;
      border-color: #ee373b;
      color: white;
    }
    .btn-primary-custom:hover {
      background-color: #cc2e32;
      border-color: #cc2e32;
    }
    .btn-secondary-custom {
      background-color: #f59e0b;
      border-color: #f59e0b;
      color: white;
    }
    .btn-secondary-custom:hover {
      background-color: #d97706;
      border-color: #d97706;
    }
    .badge-demo {
      background-color: #f59e0b !important;
    }
    .badge-preowned {
      background-color: #ee373b !important;
    }
    .form-check-input:checked {
      background-color: #ee373b;
      border-color: #ee373b;
    }
    .react-select__control { min-height: 38px; }
    .checkbox-cell { width: 5%; white-space: nowrap; padding-left: 0.5rem; padding-right: 0.5rem; }
    table.table th, table.table td { vertical-align: middle !important; }
    .react-select-container {
      display: none;
    }
    .filter-section:hover .react-select-container,
    .filter-section-expanded .react-select-container {
      display: block;
    }
    .react-select__menu {
      z-index: 10000 !important;
    }
    @media (max-width: 767px) {
      .filter-section {
        max-height: none;
      }
      .react-select-container {
        margin-bottom: 1rem;
      }
      .weekly-calendar button {
        min-width: 60px !important;
        padding: 0.5rem !important;
      }
      .weekly-calendar .text-lg {
        font-size: calc(var(--base-font-size) * 1.25) !important;
      }
      .weekly-calendar .text-xs {
        font-size: calc(var(--base-font-size) * 0.75) !important;
      }
      .filter-section .row > div {
        margin-bottom: 1rem;
      }
    }
  `;

  if (loading)
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "16rem", fontSize: "var(--base-font-size)" }}
      >
        Loading vehicles...
      </div>
    );

  if (error)
    return (
      <div
        className="d-flex align-items-center justify-content-center text-danger"
        style={{ height: "16rem", fontSize: "var(--base-font-size)" }}
      >
        Error: {error}
      </div>
    );

  return (
    <>
      <style>{extraStyles}</style>
      <div className="align-items-center p-3">
        <div
          className="w-100 max-w-4xl bg-white overflow-hidden position-relative"
          style={{
            minHeight: "82vh",
            maxHeight: "82vh",
            borderRadius: "20px",
            border: "2px solid #ee373b",
          }}
        >
          <div style={{ backgroundColor: "#ee373b", padding: "5px" }}>
            <h2 style={{ color: "white", fontWeight: "bold", textAlign: "center", margin: "0px" }} className="custom-header">
              Create Event with Selected Vehicles
            </h2>
          </div>
          <div className="h-100 px-3 d-flex flex-column position-relative">
            {currentStep === 1 && (
              <div>
                <h2
                  className="h2 mb-6"
                  style={{ color: "#ee373b", margin: "15px 0px", fontSize: "calc(var(--base-font-size) * 1.5)", textAlign: "center" }}
                >
                  Select Vehicles
                </h2>
                <div
                  className="p-3 bg-gray-50"
                  style={{
                    maxHeight: "60vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  <div
                    className={`filter-section p-3 mb-3 ${
                      filtersExpanded ? "filter-section-expanded" : ""
                    }`}
                    style={{ borderRadius: "8px", border: "1px solid #ee373b" }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <Filter size={20} color="#ee373b" />
                        <h5
                          className="fw-bold mb-0"
                          style={{ color: "#ee373b", fontSize: "calc(var(--base-font-size) * 1.25)" }}
                        >
                          Filters
                        </h5>
                        <span className="d-none d-md-inline text-danger bg-white " style={{ fontSize: "calc(var(--base-font-size) * 0.875)" }}>
                          (Point the mouse <MousePointer size={12} /> here to
                          expand filter)
                        </span>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          onClick={() => setFiltersExpanded(!filtersExpanded)}
                          className="btn btn-sm d-md-none"
                          style={{ backgroundColor: "#ee373b", color: "white", fontSize: "var(--base-font-size)" }}
                        >
                          {filtersExpanded ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                        </button>
                        <button
                          onClick={clearAllFilters}
                          className="btn btn-link p-0"
                          style={{ color: "#ee373b", fontSize: "var(--base-font-size)" }}
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <div className="react-select-container">
                      <div className="row g-3 mt-2">
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: "var(--base-font-size)" }}>Category</label>
                          <div className="btn-group w-100">
                            {["demo", "pre-owned"].map((cat) => (
                              <button
                                key={cat}
                                onClick={() => {
                                  handleFilterChange("vehicleCategory", cat);
                                  cat === "demo"
                                    ? setVehicleType("0")
                                    : setVehicleType("1");
                                }}
                                className={`btn ${
                                  filters.vehicleCategory === cat
                                    ? "btn-primary-custom"
                                    : "btn-outline-secondary"
                                }`}
                                style={
                                  filters.vehicleCategory === cat
                                    ? {
                                        backgroundColor: "#ee373b",
                                        borderColor: "#ee373b",
                                        fontSize: "var(--base-font-size)",
                                      }
                                    : {
                                        borderColor: "#ee373b",
                                        color: "#ee373b",
                                        fontSize: "var(--base-font-size)",
                                      }
                                }
                              >
                                {cat === "demo" ? "Demo" : "Pre-Owned"}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: "var(--base-font-size)" }}>Make</label>
                          <Select
                            classNamePrefix="react-select"
                            isMulti
                            options={filterOptions.makes.map((m) => ({
                              value: m,
                              label: m,
                            }))}
                            value={filters.makes.map((m) => ({
                              value: m,
                              label: m,
                            }))}
                            onChange={(selected) =>
                              handleFilterChange(
                                "makes",
                                (selected || []).map((s) => s.value)
                              )
                            }
                            styles={customSelectStyles}
                            menuPlacement="auto"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: "var(--base-font-size)" }}>Model</label>
                          <Select
                            classNamePrefix="react-select"
                            isMulti
                            options={filterOptions.models.map((m) => ({
                              value: m,
                              label: m,
                            }))}
                            value={filters.models.map((m) => ({
                              value: m,
                              label: m,
                            }))}
                            onChange={(selected) =>
                              handleFilterChange(
                                "models",
                                (selected || []).map((s) => s.value)
                              )
                            }
                            styles={customSelectStyles}
                            menuPlacement="auto"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: "var(--base-font-size)" }}>Year</label>
                          <Select
                            classNamePrefix="react-select"
                            isMulti
                            options={filterOptions.years.map((y) => ({
                              value: y,
                              label: String(y),
                            }))}
                            value={filters.years.map((y) => ({
                              value: y,
                              label: String(y),
                            }))}
                            onChange={(selected) =>
                              handleFilterChange(
                                "years",
                                (selected || []).map((s) => s.value)
                              )
                            }
                            styles={customSelectStyles}
                            menuPlacement="auto"
                          />
                        </div>
                        <div className="col-md-3">
                          <label className="form-label" style={{ fontSize: "var(--base-font-size)" }}>Max Price</label>
                          <input
                            type="number"
                            className="form-control"
                            value={filters.maxPrice}
                            onChange={(e) =>
                              handleFilterChange("maxPrice", e.target.value)
                            }
                            style={{ borderColor: "#ee373b", fontSize: "var(--base-font-size)" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white p-2 rounded shadow-sm mb-2 d-flex justify-content-between align-items-center">
                    <small style={{ fontSize: "var(--base-font-size)" }}>
                      Showing {filteredVehicles.length} of {allVehicles.length}{" "}
                      vehicles
                      {selectedVehicles.size > 0 &&
                        ` | ${selectedVehicles.size} selected`}
                    </small>
                    <div>
                      <button
                        onClick={selectAllVisible}
                        className="btn btn-link btn-sm"
                        style={{ color: "#ee373b", fontSize: "var(--base-font-size)" }}
                      >
                        Select All
                      </button>
                      {selectedVehicles.size > 0 && (
                        <button
                          onClick={deselectAll}
                          className="btn btn-link btn-sm"
                          style={{ color: "#ee373b", fontSize: "var(--base-font-size)" }}
                        >
                          Deselect
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="d-none d-md-block bg-white rounded shadow-sm">
                    <div className="table-wrapper">
                      <table className="table table-hover">
                        <thead className="table-fixed-header bg-light">
                          <tr>
                            <th className="checkbox-cell">
                              <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={
                                  filteredVehicles.length > 0 &&
                                  selectedVehicles.size ===
                                    filteredVehicles.length
                                }
                                onChange={(e) =>
                                  e.target.checked
                                    ? selectAllVisible()
                                    : deselectAll()
                                }
                                style={{ position: "static" }}
                              />
                            </th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Category</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Make</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Model</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Variant</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Year</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Color</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Price</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Mileage</th>
                            <th style={{ fontSize: "var(--base-font-size)" }}>Location</th>
                          </tr>
                        </thead>
                        <tbody className="table-scrollable-body">
                          {filteredVehicles.map((v) => (
                            <tr key={v.AuctionListId}>
                              <td className="checkbox-cell">
                                <input
                                  type="checkbox"
                                  className="form-check-input m-0"
                                  checked={selectedVehicles.has(
                                    v.AuctionListId
                                  )}
                                  onChange={() =>
                                    handleVehicleSelect(v.AuctionListId)
                                  }
                                  style={{ position: "static" }}
                                  aria-label={`Select ${v.Make} ${v.Model}`}
                                />
                              </td>
                              <td>
                                <span
                                  className={`badge ${
                                    v.VehicleCategory === "Demo"
                                      ? "badge-demo"
                                      : "badge-preowned"
                                  }`}
                                  style={{ fontSize: "calc(var(--base-font-size) * 0.875)" }}
                                >
                                  {v.VehicleCategory}
                                </span>
                              </td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Make}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Model}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Variant}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Year}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Color}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>₹{v.StartPrice}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Mileage}</td>
                              <td style={{ fontSize: "var(--base-font-size)" }}>{v.Location}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {filteredVehicles.length === 0 && (
                      <div className="text-center p-3 text-muted" style={{ fontSize: "var(--base-font-size)" }}>
                        No vehicles found
                      </div>
                    )}
                  </div>
                  <div className="d-md-none">
                    {filteredVehicles.map((v) => (
                      <div
                        key={v.AuctionListId}
                        className="card mb-2 mobile-card shadow-sm"
                      >
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h6 className="fw-bold mb-1" style={{ fontSize: "calc(var(--base-font-size) * 1.25)" }}>
                                {v.Make} {v.Model}
                              </h6>
                              <div className="text-muted" style={{ fontSize: "var(--base-font-size)" }}>
                                {v.Variant} • {v.Year}
                              </div>
                            </div>
                            <div>
                              <span
                                className={`badge ${
                                  v.VehicleCategory === "Demo"
                                    ? "badge-demo"
                                    : "badge-preowned"
                                }`}
                                style={{ fontSize: "calc(var(--base-font-size) * 0.875)" }}
                              >
                                {v.VehicleCategory}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex justify-content-between align-items-center mt-2">
                            <div>
                              <div className="mb-1" style={{ fontSize: "var(--base-font-size)" }}>
                                {v.Color} • {v.Mileage} km
                              </div>
                              <div className="fw-bold" style={{ fontSize: "calc(var(--base-font-size) * 1.25)" }}>₹{v.StartPrice}</div>
                              <small className="text-muted" style={{ fontSize: "calc(var(--base-font-size) * 0.875)" }}>{v.Location}</small>
                            </div>
                            <div className="card-select-row">
                              <input
                                type="checkbox"
                                className="form-check-input m-0"
                                checked={selectedVehicles.has(v.AuctionListId)}
                                onChange={() =>
                                  handleVehicleSelect(v.AuctionListId)
                                }
                                style={{ position: "static" }}
                                aria-label={`Select ${v.Make} ${v.Model}`}
                              />
                              <label className="mb-0" style={{ fontSize: "var(--base-font-size)" }}>Select</label>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {currentStep === 2 && (
              <div>
                <h2
                  className="h2 mb-6"
                  style={{ color: "#ee373b", margin: "15px 0px", fontSize: "calc(var(--base-font-size) * 1.5)", textAlign: "center" }}
                >
                  Select Date & Time
                </h2>
                <div className="d-flex flex-column gap-5 justify-content-center align-items-center">
                  <WeeklyCalendar
                    selectedDate={formData.selectedDate}
                    onDateSelect={handleDateSelect}
                    availableSlots={availableSlots}
                    selectedVehicles={selectedVehicles}
                    allVehicles={allVehicles}
                  />
                  {formData.selectedDate && (
                    <TimeSlots
                      timeSlots={formData.selectedTimeSlots}
                      onTimeSlotSelect={handleTimeSlotSelect}
                    />
                  )}
                  {carCount > 0 && <div className="mt-4"></div>}
                </div>
              </div>
            )}
            {currentStep === 3 && (
              <div>
                <h2
                  className="h2 mb-6"
                  style={{ color: "#ee373b", margin: "15px 0px", fontSize: "calc(var(--base-font-size) * 1.5)", textAlign: "center" }}
                >
                  Set Approval Limit & Duration
                </h2>
                <div className="space-y-6 mt-2 d-flex flex-column justify-content-center">
                  <div className="mb-3">
                    <label htmlFor="duration" className="form-label" style={{ fontSize: "var(--base-font-size)" }}>
                      Duration of Event
                    </label>
                    <select
                      id="duration"
                      value={formData.duration}
                      onChange={handleDurationChange}
                      style={{
                        maxWidth: "300px",
                        maxHeight: "100px",
                        overflowY: "auto",
                        scrollbarColor: "#ee373b #ffffff",
                        fontSize: "var(--base-font-size)",
                      }}
                      className="form-select"
                    >
                      <option value="" style={{ fontSize: "var(--base-font-size)" }}>Select Duration</option>
                      {durationOptions.length > 0 ? (
                        durationOptions.map((duration) => (
                          <option key={duration.id} value={duration.value} style={{ fontSize: "var(--base-font-size)" }}>
                            {duration.label}
                          </option>
                        ))
                      ) : (
                        <>
                          <option value="5" style={{ fontSize: "var(--base-font-size)" }}>5 Mins</option>
                          <option value="10" style={{ fontSize: "var(--base-font-size)" }}>10 Mins</option>
                          <option value="15" style={{ fontSize: "var(--base-font-size)" }}>15 Mins</option>
                          <option value="30" style={{ fontSize: "var(--base-font-size)" }}>30 Mins</option>
                          <option value="45" style={{ fontSize: "var(--base-font-size)" }}>45 Mins</option>
                          <option value="60" style={{ fontSize: "var(--base-font-size)" }}>1 Hour</option>
                          <option value="120" style={{ fontSize: "var(--base-font-size)" }}>2 Hours</option>
                          <option value="180" style={{ fontSize: "var(--base-font-size)" }}>3 Hours</option>
                          <option value="240" style={{ fontSize: "var(--base-font-size)" }}>4 Hours</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}
            {currentStep === 4 && (
              <div>
                <h2
                  className="h2 mb-6"
                  style={{ color: "#ee373b", margin: "15px 0px", fontSize: "calc(var(--base-font-size) * 1.5)", textAlign: "center" }}
                >
                  Add Customer Data
                </h2>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <button
                    className="btn btn-danger"
                    style={{ width: "150px", fontSize: "var(--base-font-size)" }}
                    onClick={() => setInputMethod(inputMethod === "manual" ? "excel" : "manual")}
                  >
                    {inputMethod === "manual" ? "Excel Upload" : "Manual Input"}
                  </button>
                </div>
                <div className="form-check mb-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="whitelistCheckbox"
                    checked={isWhitelisted}
                    onChange={(e) => {setIsWhitelisted(e.target.checked); console.log(e.target.checked)}}
                    style={{ fontSize: "var(--base-font-size)" }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor="whitelistCheckbox"
                    style={{ fontSize: "var(--base-font-size)" }}
                  >
                    Is this event whitelisted?
                  </label>
                </div>
                {inputMethod === "manual" && (
                  <div className="p-2 mb-1">
                    <div className="d-flex align-items-end justify-content-center gap-3">
                        <div>
                            <input
                            type="text"
                            className="form-control"
                            value={newUser.name}
                            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                            placeholder="Enter customer name"
                            style={{ fontSize: "var(--base-font-size)" }}
                            />
                        </div>
                        <div>
                            <input
                            type="text"
                            className="form-control"
                            value={newUser.userID}
                            onChange={(e) => setNewUser({ ...newUser, userID: e.target.value })}
                            placeholder="Enter phone number"
                            style={{ fontSize: "var(--base-font-size)" }}
                            />
                        </div>
                        <div>
                            <input
                            type="email"
                            className="form-control"
                            value={newUser.email}
                            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                            placeholder="Enter email"
                            style={{ fontSize: "var(--base-font-size)" }}
                            />
                        </div>
                        <button
                            className="btn btn-danger"
                            onClick={handleAddUser}
                            disabled={!newUser.name || !newUser.userID}
                            style={{ fontSize: "var(--base-font-size)" }}
                        >
                            Add User
                        </button>
                    </div>
                    {errorMessage && (
                      <div className="alert alert-danger mt-3" style={{ fontSize: "var(--base-font-size)" }}>
                        {errorMessage}
                      </div>
                    )}
                  </div>
                )}
                {inputMethod === "excel" && (
                  <div className="p-1 mb-1 d-flex justify-content-center">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileUpload}
                      className="form-control mb-3"
                      style={{ fontSize: "var(--base-font-size)" }}
                    />
                  </div>
                )}
                {users.length > 0 && (
                  <div className="p-1">
                    <h4 className="fw-semibold mb-1" style={{ fontSize: "calc(var(--base-font-size) * 1.25)" }}>Added Customers</h4>
                    <div style={{ maxHeight: "28vh", overflowY: "auto" }}>
                      <table className="table table-hover align-middle">
                        <thead className="table-light sticky-top">
                            <tr>
                                <th style={{ fontSize: "var(--base-font-size)" }}>Name</th>
                                <th style={{ fontSize: "var(--base-font-size)" }}>Phone Number</th>
                                <th style={{ fontSize: "var(--base-font-size)" }}>Email</th>
                                <th style={{ fontSize: "var(--base-font-size)" }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                        {users.map((user, index) => (
                            <tr key={index}>
                            <td style={{ fontSize: "var(--base-font-size)" }}>{user.name}</td>
                            <td style={{ fontSize: "var(--base-font-size)" }}>{user.userID}</td>
                            <td style={{ fontSize: "var(--base-font-size)" }}>{user.email}</td>
                            <td>
                                <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => {
                                    const updatedUsers = [...users];
                                    updatedUsers.splice(index, 1);
                                    setUsers(updatedUsers);
                                }}
                                style={{ fontSize: "calc(var(--base-font-size) * 0.875)" }}
                                >
                                Remove
                                </button>
                            </td>
                            </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
            {currentStep === 5 && !isComplete && (
              <div>
                <h2
                  className="h2 mb-6"
                  style={{ color: "#ee373b", margin: "15px 0px", fontSize: "calc(var(--base-font-size) * 1.5)", textAlign: "center" }}
                >
                  Review & Create Event
                </h2>
                <div className="space-y-4">
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Event Type:{" "}
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>
                      {EVENT_TYPES.find((t) => t.value === vehicleType)
                        ?.label || vehicleType}
                    </span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Date:{" "}
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>
                      {formData.selectedDate?.toLocaleDateString()}
                    </span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Selected Time Slots:
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>
                      {formData.selectedTimeSlots
                        ?.filter((slot) => slot.selected)
                        .map((slot) => slot.time)
                        .join(", ")}
                    </span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Location:{" "}
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>{location}</span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Duration:
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>
                      {durationOptions.length > 0
                        ? durationOptions.find(
                            (duration) => duration.value === formData.duration
                          )?.label
                        : `${formData.duration} Mins`}
                    </span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Total Customer(s):{" "}
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>{users.length}</span>
                  </p>
                  <p style={{ fontSize: "calc(var(--base-font-size) * 1.125)", fontWeight: "bold" }}>
                    Selected Vehicle(s):{" "}
                    <span style={{ fontWeight: "normal", fontSize: "var(--base-font-size)" }}>
                      {selectedVehiclesForAPI.length}
                    </span>
                  </p>
                </div>
              </div>
            )}
            {isComplete && (
              <div className="text-center mt-5">
                <div
                  className="d-inline-flex justify-content-center align-items-center bg-white mb-4"
                  style={{
                    width: "4rem",
                    height: "4rem",
                    border: "1px solid #ee373b",
                    borderRadius: "4rem",
                  }}
                >
                  <Check className="w-8 h-8 text-danger bg-white" />
                </div>
                <h2 className="h2 mb-2" style={{ fontSize: "calc(var(--base-font-size) * 1.5)" }}>Event Created Successfully!</h2>
                <p className="text-muted" style={{ fontSize: "var(--base-font-size)" }}>
                  Your event has been scheduled and invitations will be sent to
                  the selected participants.
                </p>
                {link.length > 0 ? (
                  <TextDisplayCopy link={link} />
                ) : (
                  <button
                    onClick={() => navigate("/lenderEvent")}
                    className="btn btn-danger mt-3"
                    style={{ fontSize: "var(--base-font-size)" }}
                  >
                    Manage Events
                  </button>
                )}
              </div>
            )}
            {!isComplete && (
              <div
                className="d-flex gap-2 justify-content-end p-1 mt-auto position-fixed"
                style={{
                  backgroundColor: "rgba(0,0,0,0)",
                  top: "calc(80vh + 50px)",
                  right: "calc(2vw + 15px)",
                }}
              >
                {currentStep > 1 && (
                  <button
                    onClick={handleBack}
                    className="btn btn-outline-danger"
                    style={{ fontSize: "var(--base-font-size)" }}
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="btn btn-danger"
                  disabled={!isStepValid || (currentStep === 5 && isCreating)}
                  style={{ fontSize: "var(--base-font-size)" }}
                >
                  {currentStep === 5
                    ? isCreating
                      ? "Creating..."
                      : "Create Event"
                    : "Next"}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleSelectionEventCreation;
